import { getCookieConsent } from "@/components/CookieBanner";
import { isNative } from "@/lib/appConfig";
import api from "@/lib/api";
import { isTrackableRoute } from "@/lib/trackingRoutes";

const PIXEL_ID = "27976128618675575";

let pixelLoaded = false;

// El snippet oficial de Meta va inline en el <head>, pero la CSP de index.html no
// permite 'unsafe-inline': el navegador lo bloquearia en silencio. Se inyecta como
// script externo, que es lo que la CSP si autoriza (connect.facebook.net).
function injectPixelScript() {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
}

function loadMetaPixel() {
    if (pixelLoaded) return;
    pixelLoaded = true;

    if (!window.fbq) {
        const n = function fbq() {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        window.fbq = n;
        window._fbq = window._fbq || n;
    }

    // init no manda nada por si solo; el PageView si, y solo debe salir si el
    // usuario ha aterrizado en una pagina publica.
    // Sin esto el pixel recoge por su cuenta clics y metadatos de la pagina (p. ej. el
    // formulario de /asesores). Tiene que ir antes de init para que surta efecto.
    window.fbq("set", "autoConfig", false, PIXEL_ID);
    window.fbq("init", PIXEL_ID);
    if (isTrackableRoute(window.location.pathname)) window.fbq("track", "PageView");
    injectPixelScript();
}

// Igual que GA: el pixel solo se carga si el usuario acepta cookies de marketing en
// el CookieBanner. Sin consentimiento no se descarga fbevents.js siquiera — un
// fbq('consent','revoke') seguiria contactando con Meta desde la primera visita.
// En nativo no hay banner (aplican las etiquetas de privacidad de las tiendas), asi
// que nunca hay consentimiento y el pixel nunca carga.
export function initMetaPixel() {
    if (isNative()) return;

    const consent = getCookieConsent();
    if (consent?.marketing) loadMetaPixel();

    window.addEventListener("qc_cookie_consent_change", (e) => {
        if (e.detail?.marketing) loadMetaPixel();
    });
}

export function pixelPageView() {
    if (!pixelLoaded || !window.fbq) return;
    if (!isTrackableRoute(window.location.pathname)) return;
    try {
        window.fbq("track", "PageView");
    } catch (_e) {}
}

function readCookie(name) {
    try {
        const hit = document.cookie.split("; ").find((c) => c.startsWith(name + "="));
        return hit ? hit.slice(name.length + 1) : null;
    } catch (_e) {
        return null;
    }
}

function newEventId() {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch (_e) {}
    // Fallback para WebViews antiguos: randomUUID no existe antes de Chrome 92.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/**
 * Envia un evento de conversion por los dos caminos con el MISMO event_id.
 *
 * El del navegador lo bloquean Edge, Safari, Firefox y cualquier bloqueador; el
 * del servidor (POST a nuestra propia API, que reenvia a Meta) no lo bloquea
 * nadie. Meta se queda con uno de los dos gracias al event_id compartido: sin
 * ese id, lo que pasara por ambos se contaria dos veces.
 *
 * PageView no va por aqui a proposito — es mucho volumen, no se optimiza sobre
 * el, y no compensa una llamada a la API por cada cambio de ruta.
 */
export function trackEvent(eventName, { contentName, eventId: fixedId, value, currency } = {}) {
    if (!pixelLoaded) return;

    // Purchase trae su propio id (la sesion de Stripe): es el que el servidor
    // comprueba antes de reenviarla. El resto usa uno nuevo.
    const eventId = fixedId || newEventId();

    try {
        const params = contentName ? { content_name: contentName } : {};
        if (value != null) Object.assign(params, { value, currency });
        // AppStoreClick no es un evento estandar de Meta.
        const verb = eventName === "AppStoreClick" ? "trackCustom" : "track";
        window.fbq?.(verb, eventName, params, { eventID: eventId });
    } catch (_e) {}

    api.post("/api/marketing/event", {
        event_name: eventName,
        event_id: eventId,
        // origin+pathname, nunca href: la query string puede llevar tokens o
        // parametros con datos, y no aportan nada a la atribucion.
        event_source_url: (window.location.origin + window.location.pathname).slice(0, 500),
        fbp: readCookie("_fbp"),
        fbc: readCookie("_fbc"),
        content_name: contentName || null,
    }).catch(() => {});
}
